import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams, useParams, useLocation } from 'react-router-dom';
import {
  makeStyles, mergeClasses, tokens,
  Button, Input, Dropdown, Option, ToggleButton,
} from '@fluentui/react-components';
import { Link } from 'react-router-dom';
import FurnitureFilters, { FILTER_CONFIG } from '../components/FurnitureFilters';
import { Card, EmptyState, LoadingSpinner } from '../theme/primitives';
import { iconMap, HeartFilled } from '../theme/icons';
import { API_HOST, API_BASE } from '../config.js';
import { useAuth } from '../context/AuthContext';
import { stonePalette } from '../theme/brand';
import { formatPrice } from '../utils/format';

const SearchIcon = iconMap.search;
const CloseIcon = iconMap.close;
const SearchOffIcon = iconMap.search_off;
const ExpandMoreIcon = iconMap.expand_more;
const ViewInArIcon = iconMap.view_in_ar;
const ViewQuiltIcon = iconMap.view_quilt;
const SpaceDashboardIcon = iconMap.space_dashboard;
const ShelvesIcon = iconMap.shelves;
const TvIcon = iconMap.tv;
const DresserIcon = iconMap.dresser;
const CabinetIcon = iconMap.inventory_2;
const FavoriteOutlineIcon = iconMap.favorite_border;

// Etiketė (label) versta per t('furniture_list.category_<type>') komponento viduje
// (žr. getCategoryLabel žemiau) — čia, modulio lygmenyje, negalima kviesti hook'o.
const CATEGORY_MAP = {
  'spintos-varstomomis-durimis': { type: 0 },
  'spintos-stumdomomis-durimis': { type: 1 },
  'lentynos':   { type: 2 },
  'spinteles':  { type: 3 },
  'komodos':    { type: 4 },
  'tv-spinteles': { type: 5 },
};

const PAGE_SIZE = 12;

// Griffel makeStyles — spalvos/tarpai per Fluent token'us; likusios (aspect-ratio,
// grid stulpelių skaičius per breakpointus) yra struktūrinės reikšmės be semantinio
// token atitikmens, tad lieka literalios (ta pati logika, kaip NavigationBar/FurnitureFilters).
const useStyles = makeStyles({
  page: { minHeight: '100vh', backgroundColor: stonePalette[50] },

  // ── Plain header (bendras katalogas, be kategorijos nuotraukos) ──────────
  headerBar: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  headerInner: {
    maxWidth: '80rem', marginLeft: 'auto', marginRight: 'auto',
    paddingLeft: tokens.spacingHorizontalXXL, paddingRight: tokens.spacingHorizontalXXL,
    paddingTop: tokens.spacingVerticalXL, paddingBottom: tokens.spacingVerticalXL,
    '@media (min-width: 1024px)': { paddingLeft: '3rem', paddingRight: '3rem' },
  },
  title: {
    fontSize: tokens.fontSizeBase600, lineHeight: tokens.lineHeightBase600,
    fontWeight: tokens.fontWeightBold, color: tokens.colorNeutralForeground1,
    letterSpacing: '-0.01em', margin: 0,
    '@media (min-width: 1024px)': { fontSize: tokens.fontSizeHero700, lineHeight: tokens.lineHeightHero700 },
  },

  // ── Trupinių takelis (breadcrumb) ─────────────────────────────────────────
  breadcrumb: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXXS, fontSize: tokens.fontSizeBase200, marginBottom: tokens.spacingVerticalS },
  breadcrumbLinkPlain: { color: tokens.colorNeutralForeground3, textDecorationLine: 'none', ':hover': { color: tokens.colorBrandForegroundLink } },
  breadcrumbSepPlain: { color: tokens.colorNeutralStroke2 },
  breadcrumbCurrentPlain: { color: tokens.colorNeutralForeground2 },

  searchWrap: { width: '100%', '@media (min-width: 480px)': { width: '16rem' } },
  searchInput: { width: '100%' },

  container: {
    maxWidth: '80rem', marginLeft: 'auto', marginRight: 'auto',
    paddingLeft: tokens.spacingHorizontalXXL, paddingRight: tokens.spacingHorizontalXXL,
    paddingTop: tokens.spacingVerticalL, paddingBottom: tokens.spacingVerticalL,
    '@media (min-width: 1024px)': { paddingLeft: '3rem', paddingRight: '3rem' },
  },

  // ── Puslapio tinklelis: filtrai kairėje, turinys dešinėje ────────────────
  pageGrid: {
    display: 'grid', gridTemplateColumns: '1fr', gap: tokens.spacingHorizontalXL,
    '@media (min-width: 900px)': { gridTemplateColumns: '17rem 1fr', alignItems: 'start' },
  },
  sidebar: { '@media (min-width: 900px)': { position: 'sticky', top: tokens.spacingVerticalL } },
  main: { minWidth: 0 },

  toolbarRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: tokens.spacingVerticalXL, gap: tokens.spacingHorizontalM, flexWrap: 'wrap',
  },
  countText: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground2, margin: 0 },
  toolbarActions: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  sortDropdown: { minWidth: '11rem' },

  viewToggle: {
    display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXXS,
    backgroundColor: tokens.colorNeutralBackground1, borderRadius: tokens.borderRadiusXLarge,
    padding: tokens.spacingHorizontalXXS, border: `1px solid ${tokens.colorNeutralStroke2}`,
  },

  spinnerWrap: { display: 'flex', justifyContent: 'center', alignItems: 'center', paddingTop: '5rem', paddingBottom: '5rem' },

  // Vienas @media lygis (ne trys sluoksniuoti) — žr. Home.jsx categoriesGrid komentarą
  // apie Griffel runtime tvarkos riziką; auto-fit ŠIO breakpoint'o viduje natūraliai
  // perbėga 2→3→4 stulpelius pagal turimą plotį, be papildomų konkuruojančių taisyklių.
  grid: {
    display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: tokens.spacingHorizontalL,
    '@media (min-width: 640px)': { gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' },
  },

  itemCard: { cursor: 'pointer', overflow: 'hidden', WebkitTapHighlightColor: 'transparent' },
  imageBox: { position: 'relative', aspectRatio: '1', backgroundColor: tokens.colorNeutralBackground1, overflow: 'hidden' },
  image: { width: '100%', height: '100%', transitionProperty: 'transform', transitionDuration: tokens.durationSlow, objectFit: 'contain', padding: tokens.spacingHorizontalM },
  imageCover: { objectFit: 'cover', padding: 0 },
  imageHovered: { transform: 'scale(1.05)' },
  fallback: {
    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexDirection: 'column', gap: tokens.spacingVerticalXXS, color: tokens.colorNeutralForeground3,
  },
  fallbackHint: { fontSize: '10px', color: tokens.colorNeutralStroke2 },
  // Realus Fluent <ToggleButton> — tik plūduriuojanti pozicija virš
  // nuotraukos + permatomas fonas neaktyviai būsenai (žr. #5). Dešinėje (ne
  // kairėje) — čia anksčiau buvo "Foto"/"2D"/"3D" ženkliukas, kurį ištrynėme
  // kaip nereikalingą (žr. /admin/bugs "Istrinti si label foto arba 2d, 3d
  // nes ir taip aisku, geriau vietoj jo pastumti like sirdele is kaires i
  // desine").
  favoriteBtn: {
    position: 'absolute', top: tokens.spacingVerticalSNudge, right: tokens.spacingHorizontalSNudge,
    minWidth: 'unset', width: '28px', height: '28px',
    backgroundColor: 'rgba(255,255,255,0.8)', color: tokens.colorNeutralForeground3,
    boxShadow: tokens.shadow4,
    ':hover': { backgroundColor: tokens.colorNeutralBackground1, color: tokens.colorPaletteRedForeground1 },
  },
  // Tik pati širdelė raudona/filled — BE raudono apskritimo fono aplinkui
  // (žr. /admin/bugs "turi tapti tik sirdele raudonos spalvos, filled").
  favoriteBtnActive: {
    backgroundColor: 'rgba(255,255,255,0.8)', color: tokens.colorPaletteRedForeground1,
    ':hover': { backgroundColor: tokens.colorNeutralBackground1, color: tokens.colorPaletteRedForeground1 },
  },

  // flex column + flex:1 (Card already stretches to the tallest card in its grid row) —
  // lets viewBtn anchor to the bottom via marginTop:'auto' regardless of whether the price
  // line is present, instead of sitting right after whatever content happens to precede it
  // (žr. /admin/bugs "Card mygtukai turi lygiuoti su lyg apacia").
  info: { padding: tokens.spacingHorizontalM, display: 'flex', flexDirection: 'column', flex: 1 },
  code: {
    fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1, fontSize: tokens.fontSizeBase200,
    marginBottom: tokens.spacingVerticalXXS, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    fontFamily: tokens.fontFamilyMonospace,
  },
  typeLabel: { fontSize: tokens.fontSizeBase100, color: tokens.colorNeutralForeground3, marginBottom: tokens.spacingVerticalS },
  dims: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: tokens.fontSizeBase100, color: tokens.colorNeutralForeground2, marginBottom: tokens.spacingVerticalXS },
  price: { fontSize: tokens.fontSizeBase300, fontWeight: tokens.fontWeightBold, color: tokens.colorNeutralForeground1, marginBottom: tokens.spacingVerticalXS, fontVariantNumeric: 'tabular-nums' },
  viewBtn: { marginTop: 'auto', paddingTop: tokens.spacingVerticalXXS, width: '100%' },

  loadMoreWrap: { textAlign: 'center', marginTop: tokens.spacingVerticalXXL },

  emptyRecovery: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: tokens.spacingVerticalM },
  emptySuggestLabel: {
    fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: tokens.spacingVerticalS,
  },
  emptySuggestRow: { display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: tokens.spacingHorizontalS },
  emptySuggestLink: {
    fontSize: tokens.fontSizeBase300, fontWeight: tokens.fontWeightMedium, color: tokens.colorBrandForegroundLink,
    textDecorationLine: 'underline', textUnderlineOffset: '2px',
    ':hover': { color: tokens.colorBrandForegroundLinkHover },
  },
});

const FurnitureList = () => {
  const s = useStyles();
  const { t } = useTranslation();
  const getCategoryLabel = (type) => t(`furniture_list.category_${type}`);
  const [furnitures, setFurnitures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const navigate = useNavigate();
  // Kategorijų maršrutai (/baldai/komodos ir pan.) yra STATINIAI keliai (žr. App.js) —
  // jie sąmoningai neturi :slug parametro, kad neprasilenktų su /baldai/:code
  // (FurnitureItem) maršrutu. Todėl useParams() čia visada grąžina tuščią objektą;
  // kategoriją nuskaitome iš location.pathname, o ne iš route parametro.
  const { slug: routeSlug } = useParams();
  const location = useLocation();
  const pathSlug = location.pathname.replace(/^\/baldai\/?/, '').split('/')[0] || null;
  const slug = routeSlug || pathSlug;
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();

  const category = slug ? CATEGORY_MAP[slug] : null;

  // BUG (rasta 2026-09-04): App.js `AnimatedRoutes` apgaubia kiekvieną puslapį
  // `<div key={location.key}>` (puslapio perėjimo animacijai) — `location.key`
  // pasikeičia PO KIEKVIENO `setSearchParams()` iškvietimo (net kai keičiasi
  // TIK query string, ne pathname), tad React PILNAI PERMONTUOJA FurnitureList
  // po KIEKVIENO filtro paspaudimo. Ankstesnis kodas šioje vietoje ignoruodavo
  // URL, kai `category` yra nustatyta (kategorijos puslapis) — po permontavimo
  // tai VISADA nuresetindavo `filters.type` atgal į `[category.type]`, ištrindamas
  // bet kokį papildomą "Tipas" pasirinkimą. Sprendimas: URL parametras (kai jis
  // yra) visada laimi prieš `category` numatytąją reikšmę — jis jau apima
  // category.type (žr. handleFilterChange), tad tikra kategorija niekada
  // nedingsta, o papildomi pasirinkimai išlieka po permontavimo.
  const [filters, setFilters] = useState({
    type: searchParams.get('type')
      ? searchParams.get('type').split(',').map(Number)
      : (category ? [category.type] : []),
    width: searchParams.get('width') ? searchParams.get('width').split(',') : [],
    height: searchParams.get('height') ? searchParams.get('height').split(',') : [],
    doors: searchParams.get('doors') ? searchParams.get('doors').split(',') : [],
    inner: searchParams.get('inner') ? searchParams.get('inner').split(',') : [],
    sort: searchParams.get('sort') || '',
    viewMode: searchParams.get('viewMode') || '3d',
    search: searchParams.get('search') || '',
  });

  const [hoveredId, setHoveredId] = useState(null);
  // Kai kurių senesnių/seed'intų įrašų lifestyleImagePath/image2dPath rodo į failą,
  // kurio serveryje faktiškai nėra (404) — anksčiau tokia nuotrauka tiesiog
  // dingdavo su "Nėra nuotraukos" vietrikiu, NEPABANDŽIUS grįžti prie realaus,
  // veikiančio imagePath (18 punktų sąrašo #10). Sekame, kurie src sugedę, ir
  // krentame per lifestyle → 2D → imagePath grandinę.
  const [brokenSrcs, setBrokenSrcs] = useState(() => new Set());
  const markBroken = useCallback((src) => {
    setBrokenSrcs(prev => (prev.has(src) ? prev : new Set(prev).add(src)));
  }, []);

  const [favorites, setFavorites] = useState(() => {
    try { return JSON.parse(localStorage.getItem('fitit_favorites')) || []; } catch { return []; }
  });

  useEffect(() => {
    if (!isAuthenticated) return;
    fetch(`${API_BASE}/user/favorites`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.codes) {
          setFavorites(data.codes);
          localStorage.setItem('fitit_favorites', JSON.stringify(data.codes));
        }
      })
      .catch(() => {});
  }, [isAuthenticated]);

  // BUG (rasta 2026-09-05): šis efektas anksčiau veikdavo TIESIOG kaskart, kai
  // `slug` priklausomybė "pasikeičia" — bet React (nežinia kodėl, veikiausiai
  // useSearchParams() vidinis re-render/commit elgesys) šį efektą iš naujo
  // paleisdavo IR PO KIEKVIENO filtro pakeitimo (pvz. "Tipas" varnelės),
  // NORS `slug` reikšmė realiai NEPASIKEITĖ (liko `null`/ta pati kategorija) —
  // efekto kūnas besąlygiškai perrašydavo `filters.type` atgal į
  // `category ? [category.type] : []`, ištrindamas ką tik vartotojo pasirinktą
  // "Tipas" varnelę. Sprendimas: `useRef` saugo PASKUTINĮ realiai apdorotą
  // slug — efektas nieko nedaro, jei slug nuo to karto NEPASIKEITĖ, tad
  // nebeturi reikšmės, kiek kartų React jį iškviečia.
  const prevSlugRef = useRef(slug);
  useEffect(() => {
    document.title = category ? `${getCategoryLabel(category.type)} | Fitit` : `${t('furniture_list.title')} | Fitit`;
    if (slug === prevSlugRef.current) return;
    prevSlugRef.current = slug;
    setFilters(prev => ({
      ...prev,
      type: category ? [category.type] : [],
    }));
  }, [slug, category]);

  useEffect(() => {
    fetch(`${API_BASE}/furnitures/shop`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setFurnitures(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleFilterChange = (key, value) => {
    setVisibleCount(PAGE_SIZE);
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    const params = {};
    if (newFilters.type.length) params.type = newFilters.type.join(',');
    if (newFilters.width.length) params.width = newFilters.width.join(',');
    if (newFilters.height.length) params.height = newFilters.height.join(',');
    if (newFilters.doors.length) params.doors = newFilters.doors.join(',');
    if (newFilters.inner.length) params.inner = newFilters.inner.join(',');
    if (newFilters.sort) params.sort = newFilters.sort;
    if (newFilters.viewMode && newFilters.viewMode !== '3d') params.viewMode = newFilters.viewMode;
    if (newFilters.search) params.search = newFilters.search;
    setSearchParams(params);
  };

  const toggleFavorite = useCallback((code) => {
    setFavorites(prev => {
      const next = prev.includes(code) ? prev.filter(f => f !== code) : [...prev, code];
      localStorage.setItem('fitit_favorites', JSON.stringify(next));
      return next;
    });
    if (isAuthenticated) {
      fetch(`${API_BASE}/user/favorites/toggle/${encodeURIComponent(code)}`, {
        method: 'POST',
        credentials: 'include',
      }).catch(() => {});
    }
  }, [isAuthenticated]);

  const filteredFurnitures = useMemo(() => {
    let result = [...furnitures];
    // PASTABA: anksčiau čia BUVO papildomas `if (category) result = result.filter(...)`
    // greta `filters.type` filtro žemiau — tai reiškė, kad kategorijos puslapyje
    // (pvz. /baldai/komodos) pažymėjus PAPILDOMĄ "Tipas" varnelę (kitą tipą),
    // rezultatas VIS TIEK likdavo apkarpytas iki VIEN kategorijos tipo, nes šis
    // besąlyginis filtras veikdavo NEPRIKLAUSOMAI nuo vartotojo pasirinkimo —
    // dėl to "Tipas" filtras atrodydavo "neveikiantis". `filters.type` jau
    // inicijuojamas iš `category` (žr. useState/prevSlugRef aukščiau), tad jis
    // vienas yra teisingas šaltinis — kategorijos apribojimo ČIA nebereikia.
    if (filters.type.length > 0) result = result.filter(item => filters.type.includes(item.settings?.type ?? -1));
    if (filters.width.length > 0) result = result.filter(item => {
      const w = item.settings?.generalWidth || 0;
      return filters.width.some(id => { const r = FILTER_CONFIG.widthRanges.find(r => r.id === id); return r && w >= r.min && (id === '>300' ? true : w < r.max); });
    });
    if (filters.height.length > 0) result = result.filter(item => {
      const h = item.settings?.generalHeight || 0;
      return filters.height.some(id => { const r = FILTER_CONFIG.heightRanges.find(r => r.id === id); return r && h >= r.min && (id === '>240' ? true : h < r.max); });
    });
    if (filters.doors.length > 0) result = result.filter(item => {
      let count = 0;
      if (item.settings?.mergedSections?.length) count = new Set(item.settings.mergedSections.map(s => s.groupId)).size;
      else count = item.cabinets?.length || 0;
      return filters.doors.some(c => c === '> 6' ? count > 6 : count === parseInt(c, 10));
    });
    if (filters.inner.length > 0) result = result.filter(item => {
      const opts = Object.values(item.settings?.sectionOptions || {});
      const hasDrawers = opts.some(v => [4,5,6,7].includes(v));
      const hasRacks = opts.some(v => [1,2,8,9].includes(v));
      if (filters.inner.includes('drawers') && hasDrawers) return true;
      if (filters.inner.includes('racks') && hasRacks) return true;
      return false;
    });
    if (filters.search) {
      const q = filters.search.toLowerCase();
      result = result.filter(item => item.code?.toLowerCase().includes(q));
    }
    if (filters.sort) {
      result.sort((a, b) => {
        const va = (a.settings?.generalWidth || 0) * (a.settings?.generalHeight || 0);
        const vb = (b.settings?.generalWidth || 0) * (b.settings?.generalHeight || 0);
        return filters.sort === 'price_desc' ? vb - va : va - vb;
      });
    }
    return result;
  }, [furnitures, filters]);

  const sortLabel = filters.sort === 'price_asc'
    ? t('furniture_list.sort_size_asc')
    : filters.sort === 'price_desc'
      ? t('furniture_list.sort_size_desc')
      : t('furniture_list.sort_default');

  return (
    <div className={s.page}>

      {/* Header — visada paprastas tipografinis variantas (be nuotraukos baneriio) */}
      <div className={s.headerBar}>
        <div className={s.headerInner}>
          <div className={s.breadcrumb}>
            <Link to="/" className={s.breadcrumbLinkPlain}>{t('furniture_list.breadcrumb_home')}</Link>
            <span className={s.breadcrumbSepPlain}>/</span>
            {category ? (
              <>
                <Link to="/baldai" className={s.breadcrumbLinkPlain}>{t('furniture_list.breadcrumb_catalog')}</Link>
                <span className={s.breadcrumbSepPlain}>/</span>
                <span className={s.breadcrumbCurrentPlain}>{getCategoryLabel(category.type)}</span>
              </>
            ) : (
              <span className={s.breadcrumbCurrentPlain}>{t('furniture_list.title')}</span>
            )}
          </div>
          <h1 className={s.title}>{category ? getCategoryLabel(category.type) : t('furniture_list.title')}</h1>
        </div>
      </div>

      <div className={s.container}>
      <div className={s.pageGrid}>

        {/* Filtrai — kairė šoninė juosta */}
        <aside className={s.sidebar}>
          <FurnitureFilters filters={filters} onFilterChange={handleFilterChange} layout="column" />
        </aside>

        <div className={s.main}>

        {/* Paieška + rūšiavimas + vaizdo perjungiklis */}
        <div className={s.toolbarRow}>
          <p className={s.countText}>
            {loading ? t('furniture_list.loading') : `${filteredFurnitures.length} ${filteredFurnitures.length === 1 ? t('furniture_list.count_one') : t('furniture_list.count_other')}`}
          </p>
          <div className={s.toolbarActions}>
            <div className={s.searchWrap}>
              <Input
                className={s.searchInput}
                placeholder={t('furniture_list.search_placeholder')}
                size="small"
                value={filters.search}
                onChange={(_e, data) => handleFilterChange('search', data.value)}
                contentBefore={<SearchIcon fontSize={16} />}
                contentAfter={filters.search ? (
                  <Button
                    appearance="transparent"
                    size="small"
                    icon={<CloseIcon fontSize={14} />}
                    onClick={() => handleFilterChange('search', '')}
                    aria-label={t('furniture_list.clear_search')}
                  />
                ) : undefined}
              />
            </div>
            <Dropdown
              size="small"
              className={s.sortDropdown}
              value={sortLabel}
              selectedOptions={filters.sort ? [filters.sort] : []}
              onOptionSelect={(_e, data) => handleFilterChange('sort', data.optionValue)}
            >
              <Option value="">{t('furniture_list.sort_default')}</Option>
              <Option value="price_asc">{t('furniture_list.sort_size_asc')}</Option>
              <Option value="price_desc">{t('furniture_list.sort_size_desc')}</Option>
            </Dropdown>
            <div className={s.viewToggle}>
              {[{ value: '3d', Icon: ViewInArIcon, label: '3D' }, { value: '2d', Icon: ViewQuiltIcon, label: '2D' }].map(v => (
                <ToggleButton
                  key={v.value}
                  size="small"
                  appearance={filters.viewMode === v.value ? 'primary' : 'subtle'}
                  checked={filters.viewMode === v.value}
                  icon={<v.Icon fontSize={14} />}
                  onClick={() => handleFilterChange('viewMode', v.value)}
                >
                  {v.label}
                </ToggleButton>
              ))}
            </div>
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className={s.spinnerWrap}>
            <LoadingSpinner label={t('furniture_list.loading')} />
          </div>
        ) : filteredFurnitures.length === 0 ? (
          <EmptyState
            icon={<SearchOffIcon fontSize={32} />}
            title={t('furniture_list.empty_title')}
            description={t('furniture_list.empty_desc')}
            action={(
              <div className={s.emptyRecovery}>
                <Button
                  appearance="outline"
                  onClick={() => setFilters({ type: [], width: [], height: [], doors: [], inner: [], sort: '', viewMode: '3d', search: '' })}
                >
                  {t('furniture_list.clear_all')}
                </Button>
                {/* Populiarios kategorijos kaip atsigavimo pasiūlymas (žr. 40 punktų
                    sąrašo site #9), ne vien tuščias "nieko nerasta". */}
                <div className={s.emptySuggestLabel}>{t('furniture_list.popular_categories')}</div>
                <div className={s.emptySuggestRow}>
                  {Object.entries(CATEGORY_MAP).slice(0, 4).map(([slug, cat]) => (
                    <Link key={slug} to={`/baldai/${slug}`} className={s.emptySuggestLink}>{getCategoryLabel(cat.type)}</Link>
                  ))}
                </div>
              </div>
            )}
          />
        ) : (
          <>
          <div className={s.grid}>
            {filteredFurnitures.slice(0, visibleCount).map(furniture => {
              const isHovered = hoveredId === furniture._id;
              const img2dUrl = furniture.image2dPath ? `${API_HOST}${furniture.image2dPath}` : null;
              const lifestyleUrl = furniture.lifestyleImagePath ? `${API_HOST}${furniture.lifestyleImagePath}` : null;
              const baseUrl = furniture.imagePath ? `${API_HOST}${furniture.imagePath}` : null;
              // Anksčiau užvedus pelę ANT KORTELĖS (isHovered) pati nuotrauka
              // PASIKEISDAVO (foto ↔ 2D/3D) — dabar rodomas vaizdas priklauso TIK
              // nuo viršuje esančio 3D/2D perjungiklio (filters.viewMode), hover
              // tik truputį priartina TĄ PATĮ vaizdą (žr. s.imageHovered žemiau,
              // ta pati logika kaip Home.jsx kortelėse — žr. /admin/bugs "Pakeisk
              // si efekta kuris pakeicia foto i 3d arba 2d... tik uzejus su pelyte
              // jis trupiti priartina").
              const wants2d = filters.viewMode === '2d';
              // Kiekvienas kandidatas gali realiai 404-inti (pvz. seed'inti įrašai su
              // lifestyleImagePath keliu, kuriam niekad nebuvo sugeneruotas failas) —
              // krentame per 2D → Foto → 3D grandinę, o ne iškart rodome tuščią vietrikį.
              const show2d = wants2d && img2dUrl && !brokenSrcs.has(img2dUrl);
              const showLifestyle = !show2d && lifestyleUrl && !brokenSrcs.has(lifestyleUrl);
              const showBase = !show2d && !showLifestyle && baseUrl && !brokenSrcs.has(baseUrl);
              const src = show2d ? img2dUrl : showLifestyle ? lifestyleUrl : showBase ? baseUrl : null;
              const isFavorite = favorites.includes(furniture.code);
              return (
                <Card
                  key={furniture._id}
                  hover
                  padding={0}
                  className={s.itemCard}
                  onClick={() => navigate(`/baldai/${furniture.code}`)}
                  onMouseEnter={() => setHoveredId(furniture._id)}
                  onMouseLeave={() => setHoveredId(null)}
                  onTouchStart={(e) => { e.currentTarget.style.transform = 'scale(0.97)'; e.currentTarget.style.transition = 'transform 0.1s'; }}
                  onTouchEnd={(e) => { e.currentTarget.style.transform = ''; }}
                  onTouchCancel={(e) => { e.currentTarget.style.transform = ''; }}
                >
                  {/* Image — kvadratinis (1:1), tiksliai atitinka AI sugeneruotų realistiškų
                      nuotraukų (1024x1024) formatą, kad jos rodytųsi BE apkarpymo viršuje/apačioje
                      (anksčiau čia buvo aspect-[4/3], kuris kirpo ~12% iš viršaus ir apačios). */}
                  <div className={s.imageBox}>
                    {src ? (
                      <img
                        src={src}
                        alt={furniture.code}
                        className={mergeClasses(s.image, showLifestyle && s.imageCover, isHovered && s.imageHovered)}
                        onError={() => markBroken(src)}
                      />
                    ) : (
                      <div className={s.fallback}>
                        {[0, 1].includes(furniture.settings?.type)
                          ? <SpaceDashboardIcon fontSize={44} />
                          : furniture.settings?.type === 2 ? <ShelvesIcon fontSize={44} />
                            : furniture.settings?.type === 3 ? <CabinetIcon fontSize={44} />
                              : furniture.settings?.type === 5 ? <TvIcon fontSize={44} />
                                : <DresserIcon fontSize={44} />}
                        <span className={s.fallbackHint}>{t('furniture_list.no_image')}</span>
                      </div>
                    )}
                    {/* Favorite button */}
                    <ToggleButton
                      appearance="transparent"
                      shape="circular"
                      checked={isFavorite}
                      // Fluent's ToggleButton "checked" būsenai TAIKO SAVO klasę TIESIOGIAI
                      // ikonos elementui (žr. useToggleButtonStyles.styles.js
                      // iconCheckedStyles.subtleOrTransparent) su brand spalva — ji
                      // laimėdavo prieš favoriteBtnActive nustatytą raudoną (ikona rodėsi
                      // žalia, ne raudona). Inline style turi didžiausią specifiškumą, tad
                      // patikimai nugali tą klasę (žr. /admin/bugs "Sirdele icon turi buti
                      // filled raudonas").
                      icon={isFavorite
                          ? <HeartFilled fontSize={15} style={{ color: tokens.colorPaletteRedForeground1 }} />
                          : <FavoriteOutlineIcon fontSize={15} />}
                      onClick={e => { e.stopPropagation(); toggleFavorite(furniture.code); }}
                      className={mergeClasses(s.favoriteBtn, isFavorite && s.favoriteBtnActive)}
                      title={isFavorite ? t('furniture_list.remove_favorite') : t('furniture_list.add_favorite')}
                    />
                  </div>

                  {/* Info */}
                  <div className={s.info}>
                    <div className={s.code}>{furniture.code}</div>
                    <div className={s.typeLabel}>
                      {FILTER_CONFIG.types.find(ft => ft.id === furniture.settings?.type)?.label || t('furniture_list.generic_type')}
                    </div>
                    <div className={s.dims}>
                      <span>{furniture.settings?.generalWidth ? `${(furniture.settings.generalWidth * 100).toFixed(0)} cm` : '—'}</span>
                      <span>{furniture.settings?.generalHeight ? `× ${(furniture.settings.generalHeight * 100).toFixed(0)} cm` : ''}</span>
                    </div>
                    {furniture.price > 0 && (
                      <div className={s.price}>{formatPrice(furniture.price)}</div>
                    )}
                    <Button appearance="primary" className={s.viewBtn}>
                      {t('furniture_list.view')}
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
          {visibleCount < filteredFurnitures.length && (
            <div className={s.loadMoreWrap}>
              <Button
                appearance="outline"
                size="large"
                icon={<ExpandMoreIcon fontSize={18} />}
                onClick={() => setVisibleCount(c => c + PAGE_SIZE)}
              >
                {t('furniture_list.show_more', { count: filteredFurnitures.length - visibleCount })}
              </Button>
            </div>
          )}
          </>
        )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default FurnitureList;
